/**
 * AnimalPassword Component Types
 *
 * Types for the kid-friendly animal password login system.
 * Students drag animals into 5 boxes to match the code shown by the teacher.
 */

/**
 * Available animal types for the password system
 */
export type AnimalType = 'cat' | 'dog' | 'bird' | 'fish' | 'bear' | 'rabbit' | 'frog' | 'lion';

/**
 * All available animals with their emoji representations
 */
export const ANIMALS: { type: AnimalType; emoji: string; label: string }[] = [
  { type: 'cat', emoji: '🐱', label: 'Cat' },
  { type: 'dog', emoji: '🐕', label: 'Dog' },
  { type: 'bird', emoji: '🐦', label: 'Bird' },
  { type: 'fish', emoji: '🐟', label: 'Fish' },
  { type: 'bear', emoji: '🐻', label: 'Bear' },
  { type: 'rabbit', emoji: '🐰', label: 'Rabbit' },
  { type: 'frog', emoji: '🐸', label: 'Frog' },
  { type: 'lion', emoji: '🦁', label: 'Lion' },
];

/**
 * Get emoji for an animal type
 */
export function getAnimalEmoji(type: AnimalType): string {
  const animal = ANIMALS.find((a) => a.type === type);
  return animal?.emoji ?? '❓';
}

/**
 * Get label for an animal type
 */
export function getAnimalLabel(type: AnimalType): string {
  const animal = ANIMALS.find((a) => a.type === type);
  return animal?.label ?? type;
}

/**
 * Number of boxes in the animal password
 */
export const ANIMAL_CODE_LENGTH = 5;

/**
 * Maximum number of devices per teacher license
 */
export const MAX_DEVICES = 30;

/**
 * Session expiry time in milliseconds (24 hours)
 */
export const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;

/**
 * Drag data for animal tiles
 */
export interface AnimalTileDragData {
  type: 'animal';
  animal: AnimalType;
}

/**
 * Props for the AnimalTile component
 */
export interface AnimalTileProps {
  /** The animal type */
  animal: AnimalType;
  /** Whether the tile is draggable */
  draggable?: boolean;
  /** Whether the tile is disabled */
  disabled?: boolean;
  /** Whether this tile is currently selected (for tap-and-tap mode) */
  isSelected?: boolean;
  /** Callback when tile is clicked/tapped (for tap-and-tap mode) */
  onClick?: (animal: AnimalType) => void;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
  /** Test ID */
  testId?: string;
}

/**
 * Props for the AnimalDropBox component
 */
export interface AnimalDropBoxProps {
  /** Index of this box (0-4) */
  index: number;
  /** Currently placed animal (if any) */
  animal?: AnimalType | null;
  /** Whether drops are enabled */
  dropEnabled?: boolean;
  /** Callback when an animal is dropped */
  onDrop?: (index: number, animal: AnimalType) => void;
  /** Callback when the box is clicked (to remove animal) */
  onClick?: (index: number) => void;
  /** Currently selected animal for tap-and-tap mode (if any) */
  selectedAnimal?: AnimalType | null;
  /** Callback when empty box is clicked with a selected animal */
  onPlaceSelected?: (index: number) => void;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
  /** Test ID */
  testId?: string;
}

/**
 * Props for the main AnimalPassword component
 */
export interface AnimalPasswordProps {
  /**
   * Callback when code is submitted.
   * Receives the entered code for validation (local or server-side).
   */
  onSubmit: (enteredCode: AnimalType[]) => void | Promise<void>;
  /**
   * The correct animal code to match (optional).
   * If provided, validates locally before calling onSubmit.
   * If not provided, just calls onSubmit with the entered code.
   */
  correctCode?: AnimalType[];
  /** Optional callback on incorrect attempt (local validation only) */
  onIncorrect?: () => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Size variant for tiles and boxes */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
  /** Test ID */
  testId?: string;
}

/**
 * Props for displaying the animal code (teacher view)
 */
export interface AnimalCodeDisplayProps {
  /** The animal code to display */
  code: AnimalType[];
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom class name */
  className?: string;
  /** Test ID */
  testId?: string;
}
